"use client";

import { cn } from "@gigpro/ui/tailwind/cn";
import * as React from "react";

// Types
type StepperOrientation = "horizontal" | "vertical";
type StepState = "active" | "completed" | "inactive" | "loading";
type StepIndicators = {
  active?: React.ReactNode;
  completed?: React.ReactNode;
  inactive?: React.ReactNode;
  loading?: React.ReactNode;
};

interface StepperContextValue {
  activeStep: number;
  setActiveStep: (step: number) => void;
  stepsCount: number;
  orientation: StepperOrientation;
  registerTrigger: (node: HTMLButtonElement | null) => void;
  triggerNodes: Array<HTMLButtonElement>;
  focusNext: (currentIdx: number) => void;
  focusPrev: (currentIdx: number) => void;
  focusFirst: () => void;
  focusLast: () => void;
  indicators: StepIndicators;
}

interface StepItemContextValue {
  step: number;
  state: StepState;
  isDisabled: boolean;
  isLoading: boolean;
}

const StepperContext = React.createContext<StepperContextValue | undefined>(undefined);
const StepItemContext = React.createContext<StepItemContextValue | undefined>(undefined);

function useStepper() {
  const ctx = React.useContext(StepperContext);
  if (!ctx) throw new Error("useStepper must be used within a Stepper");
  return ctx;
}

function useStepItem() {
  const ctx = React.useContext(StepItemContext);
  if (!ctx) throw new Error("useStepItem must be used within a StepperItem");
  return ctx;
}

interface StepperProps extends React.HTMLAttributes<HTMLDivElement> {
  readonly defaultValue?: number;
  readonly value?: number;
  readonly onValueChange?: (value: number) => void;
  readonly orientation?: StepperOrientation;
  readonly indicators?: StepIndicators;
}

function Stepper({
  defaultValue = 1,
  value,
  onValueChange,
  orientation = "horizontal",
  className,
  children,
  indicators = {},
  ...props
}: Readonly<StepperProps>) {
  const [activeStep, setActiveStep] = React.useState(defaultValue);
  const [triggerNodes, setTriggerNodes] = React.useState<Array<HTMLButtonElement>>([]);

  const registerTrigger = (node: HTMLButtonElement | null) => {
    setTriggerNodes((prev) => {
      if (node && !prev.includes(node)) {
        return [...prev, node];
      }
      return prev;
    });
  };

  const handleSetActiveStep = (step: number) => {
    if (value === undefined) {
      setActiveStep(step);
    }
    onValueChange?.(step);
  };

  const currentStep = value ?? activeStep;

  const focusTrigger = (idx: number) => {
    if (triggerNodes[idx]) triggerNodes[idx].focus();
  };

  const contextValue: StepperContextValue = {
    activeStep: currentStep,
    setActiveStep: handleSetActiveStep,
    // oxlint-disable-next-line react/no-react-children -- children must be inspected to render
    stepsCount: React.Children.toArray(children).filter(
      (child): child is React.ReactElement =>
        React.isValidElement(child) && (child.type as { displayName?: string }).displayName === "StepperItem",
    ).length,
    orientation,
    registerTrigger,
    focusNext: (currentIdx) => focusTrigger((currentIdx + 1) % triggerNodes.length),
    focusPrev: (currentIdx) => focusTrigger((currentIdx - 1 + triggerNodes.length) % triggerNodes.length),
    focusFirst: () => focusTrigger(0),
    focusLast: () => focusTrigger(triggerNodes.length - 1),
    triggerNodes,
    indicators,
  };

  return (
    <StepperContext.Provider value={contextValue}>
      <div
        role="tablist"
        aria-orientation={orientation}
        data-slot="stepper"
        className={cn("w-full", className)}
        data-orientation={orientation}
        {...props}
      >
        {children}
      </div>
    </StepperContext.Provider>
  );
}

interface StepperItemProps extends React.HTMLAttributes<HTMLDivElement> {
  readonly step: number;
  readonly completed?: boolean;
  readonly disabled?: boolean;
  readonly loading?: boolean;
}

function StepperItem({
  step,
  completed = false,
  disabled = false,
  loading = false,
  className,
  children,
  ...props
}: Readonly<StepperItemProps>) {
  const { activeStep } = useStepper();

  let state: StepState = "inactive";
  if (completed || step < activeStep) {
    state = "completed";
  } else if (activeStep === step) {
    state = "active";
  }

  const isLoading = loading && step === activeStep;

  const contextValue: StepItemContextValue = { step, state, isDisabled: disabled, isLoading };

  return (
    <StepItemContext.Provider value={contextValue}>
      <div
        data-slot="stepper-item"
        className={cn(
          "group/step flex flex-1 items-center justify-center group-data-[orientation=horizontal]/stepper-nav:flex-row group-data-[orientation=vertical]/stepper-nav:flex-col",
          className,
        )}
        data-state={state}
        {...(isLoading ? { "data-loading": true } : {})}
        {...props}
      >
        {children}
      </div>
    </StepItemContext.Provider>
  );
}
StepperItem.displayName = "StepperItem";

interface StepperTriggerProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  readonly asChild?: boolean;
}

function StepperTrigger({ asChild = false, className, children, tabIndex, ...props }: Readonly<StepperTriggerProps>) {
  const { state, isLoading } = useStepItem();
  const stepperCtx = useStepper();
  const { setActiveStep, activeStep, registerTrigger, triggerNodes, focusNext, focusPrev, focusFirst, focusLast } =
    stepperCtx;
  const { step, isDisabled } = useStepItem();
  const isSelected = activeStep === step;
  const id = `stepper-tab-${step}`;
  const panelId = `stepper-panel-${step}`;

  const btnRef = React.useRef<HTMLButtonElement>(null);
  React.useEffect(() => {
    if (btnRef.current) {
      registerTrigger(btnRef.current);
    }
  }, [registerTrigger]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    const myIdx = triggerNodes.indexOf(e.currentTarget);
    switch (e.key) {
      case "ArrowRight":
      case "ArrowDown":
        e.preventDefault();
        if (myIdx !== -1 && focusNext) focusNext(myIdx);
        break;
      case "ArrowLeft":
      case "ArrowUp":
        e.preventDefault();
        if (myIdx !== -1 && focusPrev) focusPrev(myIdx);
        break;
      case "Home":
        e.preventDefault();
        if (focusFirst) focusFirst();
        break;
      case "End":
        e.preventDefault();
        if (focusLast) focusLast();
        break;
      case "Enter":
      case " ":
        e.preventDefault();
        setActiveStep(step);
        break;
    }
  };

  if (asChild) {
    return (
      <span data-slot="stepper-trigger" data-state={state} className={className}>
        {children}
      </span>
    );
  }

  let computedTabIndex = -1;
  if (typeof tabIndex === "number") {
    computedTabIndex = tabIndex;
  } else if (isSelected) {
    computedTabIndex = 0;
  }

  return (
    <button
      type="button"
      ref={btnRef}
      role="tab"
      id={id}
      aria-selected={isSelected}
      aria-controls={panelId}
      tabIndex={computedTabIndex}
      data-slot="stepper-trigger"
      data-state={state}
      data-loading={isLoading}
      className={cn(
        "inline-flex cursor-pointer items-center gap-3 rounded-full outline-none focus-visible:ring-2 focus-visible:ring-brand/50 disabled:pointer-events-none disabled:opacity-60",
        className,
      )}
      onClick={() => setActiveStep(step)}
      onKeyDown={handleKeyDown}
      disabled={isDisabled}
      {...props}
    >
      {children}
    </button>
  );
}

function StepperIndicator({ children, className }: Readonly<React.ComponentProps<"div">>) {
  const { state, isLoading } = useStepItem();
  const { indicators } = useStepper();

  return (
    <div
      data-slot="stepper-indicator"
      data-state={state}
      className={cn(
        "relative flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-light-gray text-extraSmall text-medium-gray data-[state=active]:bg-success data-[state=active]:text-white data-[state=completed]:bg-success data-[state=completed]:text-white",
        className,
      )}
    >
      {(children || indicators) && (
        <div className="absolute">
          {indicators &&
          ((isLoading && indicators.loading) ||
            (state === "completed" && indicators.completed) ||
            (state === "active" && indicators.active) ||
            (state === "inactive" && indicators.inactive))
            ? (isLoading && indicators.loading) ||
              (state === "completed" && indicators.completed) ||
              (state === "active" && indicators.active) ||
              (state === "inactive" && indicators.inactive)
            : children}
        </div>
      )}
    </div>
  );
}

function StepperSeparator({ className }: Readonly<React.ComponentProps<"div">>) {
  const { state } = useStepItem();

  return (
    <div
      data-slot="stepper-separator"
      data-state={state}
      className={cn(
        "rounded-full bg-light-gray group-data-[orientation=horizontal]/stepper-nav:h-1 group-data-[orientation=horizontal]/stepper-nav:flex-1 group-data-[orientation=vertical]/stepper-nav:h-12 group-data-[orientation=vertical]/stepper-nav:w-0.5 data-[state=active]:bg-success data-[state=completed]:bg-success",
        className,
      )}
    />
  );
}

function StepperTitle({ children, className }: Readonly<React.ComponentProps<"h3">>) {
  const { state } = useStepItem();

  return (
    <h3 data-slot="stepper-title" data-state={state} className={cn("text-small leading-none font-semibold", className)}>
      {children}
    </h3>
  );
}

function StepperDescription({ children, className }: Readonly<React.ComponentProps<"div">>) {
  const { state } = useStepItem();

  return (
    <div data-slot="stepper-description" data-state={state} className={cn("text-small text-medium-gray", className)}>
      {children}
    </div>
  );
}

function StepperNav({ children, className }: Readonly<React.ComponentProps<"nav">>) {
  const { activeStep, orientation } = useStepper();

  return (
    <nav
      data-slot="stepper-nav"
      data-state={activeStep}
      data-orientation={orientation}
      className={cn(
        "group/stepper-nav inline-flex gap-1 data-[orientation=horizontal]:w-full data-[orientation=horizontal]:flex-row data-[orientation=vertical]:flex-col",
        className,
      )}
    >
      {children}
    </nav>
  );
}

function StepperPanel({ children, className }: Readonly<React.ComponentProps<"div">>) {
  const { activeStep } = useStepper();

  return (
    <div data-slot="stepper-panel" data-state={activeStep} className={cn("w-full", className)}>
      {children}
    </div>
  );
}

interface StepperContentProps extends React.ComponentProps<"div"> {
  readonly value: number;
  readonly forceMount?: boolean;
}

function StepperContent({ value, forceMount, children, className }: Readonly<StepperContentProps>) {
  const { activeStep } = useStepper();
  const isActive = value === activeStep;

  if (!forceMount && !isActive) {
    return null;
  }

  return (
    <div
      data-slot="stepper-content"
      data-state={activeStep}
      className={cn("w-full", className, !isActive && forceMount && "hidden")}
      hidden={!isActive && forceMount}
    >
      {children}
    </div>
  );
}

export {
  useStepper,
  useStepItem,
  Stepper,
  StepperItem,
  StepperTrigger,
  StepperIndicator,
  StepperSeparator,
  StepperTitle,
  StepperDescription,
  StepperPanel,
  StepperContent,
  StepperNav,
  type StepperProps,
  type StepperItemProps,
  type StepperTriggerProps,
  type StepperContentProps,
};
